import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import {
  Button,
  TablePagination,
  Typography,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Modal from "../Modal";
import Toast from "../../../utils/toast";
import createInstance from "../../../api/axiosinstance";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

// Search bar imports
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";

const aiDishaApi = createInstance("DISHA");

const CssFormControl = styled(FormControl)({
  "& .MuiInputBase-root": {
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: 10 },
  },
});

const CssSelect = styled(Select)({
  "& .MuiSvgIcon-root": { textAlign: "center" },
});

const CityManagement = () => {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [activeCityList, setActiveCityList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deactivateId, setDeactiveId] = useState("");
  const [actionType, setActionType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { COLORS } = useThemeMode();

  useEffect(() => {
    fetchActiveStates();
    fetchActiveCities();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const fetchActiveStates = async () => {
    try {
      const response = await aiDishaApi.get("/Get_active_states");
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching active states:", error);
    }
  };

  const fetchDistrictsForState = async (state) => {
    setSelectedState(state);
    setSelectedDistrict("");
    setSelectedCity("");
    setCities([]);
    
    try {
      const districtResponse = await aiDishaApi.get(
        `/fetch_active_district_by_state?statename=${encodeURIComponent(state)}`
      );
      setDistricts(districtResponse.data);
    } catch (error) {
      console.error("Error fetching districts:", error);
      Toast.error(error.response?.data?.detail || "Failed to load districts");
    }
  };

  const fetchCitiesForDistrict = async (districtName) => {
    setSelectedDistrict(districtName);
    setSelectedCity(""); 
    
    try {
      const response = await aiDishaApi.get(`/filter_cities_by_district?district_name=${encodeURIComponent(districtName)}`);
      if (Array.isArray(response.data)) {
        setCities(response.data);
      } else {
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities for district:", error);
      setCities([]);
    }
  };

  const fetchActiveCities = async () => {
    try {
      const response = await aiDishaApi.get("/Cities/all");
      setActiveCityList(response.data);
    } catch (error) {
      console.error("Error fetching active cities", error);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalClose = () => setIsModalOpen(false);
  const handleModalOpen = (cityId, action) => {
    setDeactiveId(cityId);
    setActionType(action);
    setIsModalOpen(true);
  };
  
  const handleDeactivateCity = async (cityId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      const response = await aiDishaApi.patch(`/Cities/${cityId}/active-inactive?status=${newStatus}`);
      if (response.status === 200) {
        Toast.success(`City ${newStatus === "Active" ? "activated" : "deactivated"} successfully`);
        fetchActiveCities();
      }
    } catch (error) {
      console.error("Error toggling city status:", error);
      Toast.error("Failed to update city status");
    }
    setIsModalOpen(false);
  };

  const header = [
    { city_id: "City ID", city_name: "City Name", state_name: "State Name", district_name: "District Name", creation_date: "Creation Date", action: "Action" },
  ];

  const filteredRows = activeCityList.filter(
    (row) =>
      (row.State_name && row.State_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.city_name && row.city_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.district_name && row.district_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  const handleClear = () => {
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedCity("");
    setCities([]);
    setSearchTerm("");
  };

  const handleCitySubmit = async (event) => {
    event.preventDefault();
    try {
      if (!selectedState || !selectedState.trim()) {
        Toast.error("State name cannot be empty"); return;
      }
      if (!selectedDistrict || !selectedDistrict.trim()) {
        Toast.error("District name cannot be empty"); return;
      }
      if (!selectedCity || !selectedCity.trim()) {
        Toast.error("Please select a city"); return;
      }

      const response = await aiDishaApi.post(
        `/Cities?state_name=${selectedState}&district=${selectedDistrict}&city=${selectedCity}`,
        { Status: "Active" }
      );

      if (response.status === 200) {
        Toast.success("City saved successfully");
        handleClear();
        fetchActiveCities();
      }
    } catch (error) {
      Toast.error(error.response?.data?.detail || "Failed to save city");
    }
  };

  return (
    <div>
      <Box sx={{ marginTop: "30px", background: COLORS.primary, borderRadius: "10px", padding: "15px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}>Add City</Typography>
            
          </Box>
          
          <Paper 
            sx={{ 
              p: "2px 4px", 
              display: "flex", 
              width: "350px", 
              borderRadius: 2, 
              backgroundColor: "#ffffff",
              border: "1px solid #cccccc", 
              boxShadow: "none"
            }}
          >
            <IconButton sx={{ p: "8px", color: "#555555" }}>
              <SearchIcon />
            </IconButton>
            <InputBase
                  sx={{ 
                    ml: 1, 
                    flex: 1, 
                    fontFamily: "Poppins", 
                    color: "#000000",
                    caretColor: "#000000",
                    "& input": {
                      color: "#000000 !important",
                      "-webkit-text-fill-color": "#000000 !important",
                    },
                    "& input::placeholder": { 
                      color: "#333333 !important",
                      opacity: "1 !important", 
                    } 
                  }}
                  placeholder="Search by District or State Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
          </Paper>
        </Box>

        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" sx={{ alignItems: "center", background: COLORS.primary, borderRadius: "10px" }}>
          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "10px" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>State Name <span style={{ color: "red" }}>*</span></Typography>
            <CssFormControl sx={{ width: "90%" }}>
              <InputLabel sx={{ display: "none" }}>Select State</InputLabel>
              <CssSelect
                size="small"
                value={selectedState}
                displayEmpty
                onChange={(e) => fetchDistrictsForState(e.target.value)}
                sx={{
                  width: "95%", color: COLORS.text, background: COLORS.background, borderRadius: 2, height: "35px",
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: COLORS.border, borderRadius: 2 } },
                  "& .MuiInputBase-input": { padding: "12px 14px" },
                }}
              >
                <MenuItem value="" disabled>Select State</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.state_id} value={state.state_name}>{state.state_name}</MenuItem>
                ))}
              </CssSelect>
            </CssFormControl>
          </Box>

          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "10px" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>District Name <span style={{ color: "red" }}>*</span></Typography>
            <CssFormControl sx={{ width: "90%" }}>
              <InputLabel sx={{ display: "none" }}>Select District</InputLabel>
              <CssSelect
                size="small"
                displayEmpty
                value={selectedDistrict}
                onChange={(e) => fetchCitiesForDistrict(e.target.value)}
                sx={{
                  width: "95%", color: COLORS.text, background: COLORS.background, borderRadius: 2, height: "35px",
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: COLORS.border, borderRadius: 2 } },
                  "& .MuiSelect-select": { padding: "12px 14px !important", textAlign: "left" },
                }}
              >
                <MenuItem value="" disabled>District Name</MenuItem>
                {districts.map((district) => (
                  <MenuItem key={district.District_Id} value={district.district_name} sx={{ color: "#033538" }}>
                    {district.district_name}
                  </MenuItem>
                ))}
              </CssSelect>
            </CssFormControl>
          </Box>

          <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "10px" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>City Name <span style={{ color: "red" }}>*</span></Typography>
            <CssFormControl sx={{ width: "90%" }}>
              <InputLabel sx={{ display: "none" }}>Select City</InputLabel>
              <CssSelect
                size="small"
                displayEmpty
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                sx={{
                  width: "95%", color: COLORS.text, background: COLORS.background, borderRadius: 2, height: "35px",
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: COLORS.border, borderRadius: 2 } },
                  "& .MuiSelect-select": { padding: "12px 14px !important", textAlign: "left" },
                }}
              >
                <MenuItem value="" disabled>City Name</MenuItem>
                {cities && cities.length > 0 ? (
                  cities.map((city, index) => {
                    const cityName = typeof city === 'string' ? city : city.city_name;
                    return (
                      <MenuItem key={index} value={cityName} sx={{ color: "#033538" }}>
                        {cityName}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem value="" disabled>No cities available</MenuItem>
                )}
              </CssSelect>
            </CssFormControl>
          </Box>
          <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "center", marginBottom: "10px" }}>
            <Button onClick={handleClear} sx={{ border: "1px solid", color: COLORS.button, "&:hover": { color: COLORS.hover_btn }, width: "120px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }}>
              Clear
            </Button>
          </Box>
          <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "center", marginBottom: "10px" }}>
            <Button onClick={handleCitySubmit} sx={{ border: "1px solid", background: COLORS.button, "&:hover": { background: COLORS.hover_btn }, color: COLORS.background, width: "120px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      <Box xs={1} sx={{ mt: 4 }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer component={Paper} sx={{ borderRadius: "10px", backgroundColor: "transparent", boxShadow: "none" }}>
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  <TableRow>
                    {header.map((n) => (
                      <React.Fragment key={n.city_id}>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.city_id}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.city_name}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.state_name}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.district_name}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.creation_date}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.action}</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                      <TableRow key={row.City_Id} sx={{ backgroundColor: COLORS.background }}>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.City_Id || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.city_name || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.State_name || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.district_name || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.Creation_Date || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor: row.Status === "Active" ? COLORS.button_red_bg : COLORS.button_green_bg,
                              color: COLORS.text,
                              fontFamily: "poppins",
                              height: "24px",
                              padding: "2px 8px",
                              minWidth: "90px",
                              fontSize: "0.75rem",
                              "&:hover": { background: row.Status === "Active" ? COLORS.hover_red_btn : COLORS.hover_green_btn },
                            }}
                            onClick={() => handleModalOpen(row.City_Id, row.Status)}
                          >
                            {row.Status === "Active" ? "Deactivate" : "Activate"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow sx={{ background: COLORS.background }}>
                      <TableCell colSpan={6} sx={{ textAlign: "center", color: COLORS.text, padding: "20px" }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ background: COLORS.primary, color: COLORS.text }}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Box>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="city"
        action={() => handleDeactivateCity(deactivateId, actionType)}
        actionType={actionType === "Active" ? "Deactivate" : "Activate"}
      />
    </div>
  );
};

export default CityManagement;